// =====================================================================
// Camada de API do portal do estudante (UEB).
//
// Os PATHS espelham 1:1 os do UEN — o backend do UEB vai ser um port do
// uenserver (última etapa). Enquanto o backend não expõe essas rotas,
// estas funções existem e a UI já está ligada; as chamadas só falham até
// o backend subir. NÃO inventar contrato aqui: manter igual ao UEN.
// =====================================================================
import axios from 'axios';
import api from './client';
import { VIACEP_URL, currentServer } from '../config';

// ---------------------------------------------------------------------
// AUTH — login / registro / verificação de CPF e email
// ---------------------------------------------------------------------
export const loginEmailSenhaApi = ({ email, password }) =>
    api.post('/api/loginestudantilemailpassw', { email: String(email).trim(), password });

const local = 'http://localhost:3002'
const host = 'https://api.uebcarteirinha.com.br'
export const verifyCpfExistApi = ({ cpf }) =>
    api.post('/api/verifyCpfExist', { cpf: String(cpf).replace(/\D/g, '') });

export const verifyEmailExistApi = ({ userEmail }) =>
    api.post('/api/verifyEmailExist', { userEmail: String(userEmail).trim().toLowerCase() });

// Cria a conta (chamado no momento do pagamento, no wizard de registro).
export const registerUserApi = (dados) =>
    api.post('/api/registerUserSistemUEN', dados);

// ---------------------------------------------------------------------
// EMAIL — confirmação, reset de senha, descadastro
// ---------------------------------------------------------------------
export const validateEmailTokenApi = ({ token }) =>
    api.get(`/api/auth/email/validate?token=${encodeURIComponent(token)}`);

export const confirmEmailApi = ({ token }) =>
    api.post('/api/auth/email/confirm', { token });

export const resendEmailVerificationApi = () =>
    api.post('/api/auth/email/resend');

export const requestPasswordResetApi = ({ email }) =>
    api.post('/api/auth/password/request', { email: String(email).trim() });

export const validatePasswordResetTokenApi = ({ token }) =>
    api.get(`/api/auth/password/validate?token=${encodeURIComponent(token)}`);

export const confirmPasswordResetApi = ({ token, password }) =>
    api.post('/api/auth/password/confirm', { token, password });

export const checkUnsubscribeApi = ({ uid, sig }) =>
    api.get(`/api/auth/email/unsubscribe/check?uid=${encodeURIComponent(uid)}&sig=${encodeURIComponent(sig)}`);

export const unsubscribeEmailApi = ({ uid, sig }) =>
    api.post('/api/auth/email/unsubscribe', { uid, sig });

export const resubscribeEmailApi = ({ uid, sig }) =>
    api.post('/api/auth/email/resubscribe', { uid, sig });

// ---------------------------------------------------------------------
// RECUPERAR CONTA — aluno antigo (só CPF) ativa email + senha
// ---------------------------------------------------------------------
export const recuperarContaStatusApi = ({ cpf }) =>
    api.get(`/api/auth/recuperar-conta/status?cpf=${encodeURIComponent(String(cpf).replace(/\D/g, ''))}`);

export const recuperarContaIniciarApi = ({ cpf, dataNascimento, email, senha }) =>
    api.post('/api/auth/recuperar-conta/iniciar', {
        cpf: String(cpf).replace(/\D/g, ''), dataNascimento, email, senha,
    });

export const recuperarContaConfirmarApi = ({ token }) =>
    api.post('/api/auth/recuperar-conta/confirmar', { token });

// ---------------------------------------------------------------------
// PERFIL
// ---------------------------------------------------------------------
export const getProfileApi = () => api.get('/api/profile');

// Reenvia foto e/ou comprovante de uma carteirinha reprovada -> volta pra análise.
export const reenviarCarteirinhaApi = ({ foto, comprovante }) =>
    api.post('/api/carteirinha/reenviar', {
        ...(foto ? { foto } : {}),
        ...(comprovante ? { comprovante } : {}),
    });

// ---------------------------------------------------------------------
// PRODUTOS
// ---------------------------------------------------------------------
export const getProdutosDisponiveisApi = () => api.get('/api/produtos-disponiveis');

// Catálogo público de cursos da FUNEMG EAD (nome, descrição, capa) — usado no
// cadastro quando o aluno liga "Matricular na FUNEMG".
export const listarCursosFunemgApi = () => api.get('/api/ead/cursos-publico');

// Portal do aluno: minhas matrículas no EAD + comprovante de matrícula (PNG).
export const minhasMatriculasEadApi = () => api.get('/api/ead/minhas-matriculas');
export const baixarMeuComprovanteApi = ({ cursoId }) =>
    api.get('/api/ead/meu-comprovante', { params: { cursoId }, responseType: 'blob' });
export const getProdutosApi = () => api.get('/produtos');

// ---------------------------------------------------------------------
// FRETE (Correios) — cotação por CEP
// ---------------------------------------------------------------------
export const calcularFreteApi = ({ cepDestino }) =>
    api.post('/api/frete/calcular', { cepDestino: String(cepDestino).replace(/\D/g, '') });

// ---------------------------------------------------------------------
// SERVIÇOS EXTERNOS (fora do backend — não usam o client axios nem o token)
// ---------------------------------------------------------------------
// ViaCEP: CEP -> endereço. Resolve com o objeto do ViaCEP ou null (nunca
// rejeita nem lança), pra chamada ficar simples no componente.
export const buscarEnderecoPorCepApi = async ({ cep }) => {
    const digitos = String(cep || '').replace(/\D/g, '');
    if (digitos.length !== 8) return null;
    try {
        const res = await fetch(`${VIACEP_URL}/${digitos}/json/`);
        const dados = await res.json();
        return dados && !dados.erro ? dados : null;
    } catch {
        return null;
    }
};

// ---------------------------------------------------------------------
// PAGAMENTO — Pix (Pagar.me)
// ---------------------------------------------------------------------
export const createPixPaymentApi = ({ productId, userId, cepDestino, codigoServico, dadosCadastro }) =>
    api.post('/api/create-pix-payment', { productId, userId, cepDestino, codigoServico, dadosCadastro });

// Cartão de crédito (Pagar.me). `cardToken` vem da tokenização no navegador.
export const getPagamentoConfigApi = () => api.get('/api/pagamento/config');

export const createCardPaymentApi = ({ productId, cardToken, installments, billing, cepDestino, codigoServico, dadosCadastro }) =>
    api.post('/api/create-card-payment', { productId, cardToken, installments, billing, cepDestino, codigoServico, dadosCadastro });

export const getPaymentStatusByIdApi = ({ paymentId }) =>
    api.get(`/api/payments/status/${paymentId}`);

export const getPaymentDetailsApi = ({ paymentId }) =>
    api.get(`/api/payments/getpaymentdetails/${paymentId}`);

export const verifyPaymentApi = ({ transactionId }) =>
    api.post('/api/payments/verify', { transactionId });

export const cancelPixPaymentApi = ({ paymentId }) =>
    api.post('/api/payments/cancel', { paymentId });

// ---------------------------------------------------------------------
// HISTÓRICO / RASTREAMENTO
// ---------------------------------------------------------------------
export const getHistoricoPagamentoApi = () => api.get('/payments/history');

export const getMinhasSolicitacoesApi = () => api.get('/api/minhas-solicitacoes');

export const updateSolicitacaoImpressaoApi = ({ impressaoId, endereco }) =>
    api.put(`/api/impressoes/${impressaoId}`, endereco);

// ---------------------------------------------------------------------
// VALIDADOR PÚBLICO (legado) — usado pela página ValidadorUEB (QR Code).
//
// Rotas PÚBLICAS (sem login): por isso usam `axios` puro com currentServer,
// NÃO o client `api` de ./client. O client tem interceptor de 401 -> logout
// -> redirect pra /login; numa página pública isso jogaria o visitante pra
// tela de login por engano. Aqui só queremos a resposta crua do backend.
// Contrato idêntico ao da main: NÃO alterar sem alinhar com o backend.
// ---------------------------------------------------------------------
export const getUserByCPFAndCodUsoApi = async ({ cpf, codUso }) =>
    axios.post(`${currentServer}/api/getUserByCPFAndCodUso`, { cpf, codigo_uso: codUso });

export const getImagemByUserId = async ({ id }) =>
    axios.get(`${currentServer}/api/imagem/${id}`);
